/**
 * SOPipelineHealthCard — Dashboard card showing Won deal SO coverage.
 * Visualizes which Won deals are missing Sales Orders, value at risk, and aging.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { crmAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { AlertTriangle, CheckCircle, ShoppingCart, TrendingUp, Users, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { buildHealthCardParams } from './healthCardParams';
import { HealthFilterScopeRibbon } from './HealthFilterScopeRibbon';

// ── Animated ring/donut chart ──────────────────────────────────────────────────
function CoverageRing({ pct, size = 120, strokeWidth = 12 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  const color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#dc2626';

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      {/* Background ring */}
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
      {/* Filled ring */}
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${filled} ${circ - filled}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 1s ease' }}
      />
    </svg>
  );
}

// ── Aging bar ──────────────────────────────────────────────────────────────────
function AgingBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round(count / total * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground w-14 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="font-semibold w-6 text-right">{count}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function SOPipelineHealthCard({ compact = false, filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // 'overview' | 'missing' | 'owners'
  const { formatCurrency, currency } = useCurrency();
  const navigate = useNavigate();
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    crmAPI.getSoPipelineHealth(requestParams)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  if (loading) {
    return (
      <div className="h-full rounded-2xl overflow-hidden border border-slate-100" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <div className="p-4 space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <div className="grid grid-cols-3 gap-2">{[...Array(3)].map((_,i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, aging, top_missing = [], by_owner = [] } = data;
  const { total_won = 0, total_won_value = 0, with_so_count = 0, without_so_count = 0,
          without_so_value = 0, coverage_pct = 0, avg_days_no_so = 0 } = summary || {};

  const riskColor = coverage_pct >= 80 ? 'text-emerald-600' : coverage_pct >= 60 ? 'text-amber-600' : 'text-red-600';
  const ringColor = coverage_pct >= 80 ? '#10b981' : coverage_pct >= 60 ? '#f59e0b' : '#dc2626';

  return (
    <div className="h-full rounded-2xl overflow-hidden border border-slate-100 bg-white flex flex-col"
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
      data-testid="so-pipeline-health-card">

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <ShoppingCart className="h-4 w-4 text-[#800000]" />
            SO Pipeline Health
          </h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">Won deals — Sales Order coverage</p>
          <div className="mt-2"><HealthFilterScopeRibbon filters={filters} /></div>
        </div>
        {/* View tabs */}
        <div className="flex gap-0.5">
          {['overview','missing','owners'].map(v => (
            <button key={v} onClick={() => setActiveView(v)}
              className={`px-2 py-1 text-[10px] font-semibold rounded-lg transition-all capitalize ${activeView === v ? 'bg-[#800000] text-white' : 'text-muted-foreground hover:bg-slate-100'}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* OVERVIEW tab */}
      {activeView === 'overview' && (
        <div className="px-4 pb-4 flex-1 overflow-hidden">
          {/* Coverage ring + KPIs */}
          <div className="flex items-center gap-4 mb-3">
            <div className="relative shrink-0">
              <CoverageRing pct={coverage_pct} size={100} strokeWidth={10} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-xl font-black ${riskColor}`}>{coverage_pct}%</span>
                <span className="text-[9px] text-muted-foreground">coverage</span>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 bg-emerald-50 border border-emerald-100">
                  <p className="text-[10px] text-emerald-600 font-medium">With SO</p>
                  <p className="text-lg font-black text-emerald-700">{with_so_count}</p>
                  <p className="text-[9px] text-emerald-500 font-mono">{formatCurrency(total_won_value - without_so_value, currency)}</p>
                </div>
                <div className="rounded-xl p-2.5 bg-red-50 border border-red-100">
                  <p className="text-[10px] text-red-600 font-medium">Missing SO</p>
                  <p className="text-lg font-black text-red-700">{without_so_count}</p>
                  <p className="text-[9px] text-red-500 font-mono">{formatCurrency(without_so_value, currency)}</p>
                </div>
              </div>
              {without_so_count > 0 && (
                <div className="rounded-xl p-2.5" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #9f1239 100%)' }}>
                  <p className="text-[10px] text-white/80">Value at Risk</p>
                  <p className="text-base font-black text-white font-mono">{formatCurrency(without_so_value, currency)}</p>
                  <p className="text-[9px] text-white/60">Avg {avg_days_no_so}d since won</p>
                </div>
              )}
            </div>
          </div>

          {/* Aging breakdown */}
          <div className="rounded-xl bg-slate-50 p-3 space-y-1.5">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Missing SO Aging</p>
            <AgingBar label="0–30d" count={aging?.['0_30'] || 0} total={without_so_count} color="bg-amber-400" />
            <AgingBar label="31–60d" count={aging?.['31_60'] || 0} total={without_so_count} color="bg-orange-500" />
            <AgingBar label="61–90d" count={aging?.['61_90'] || 0} total={without_so_count} color="bg-red-500" />
            <AgingBar label="90d+" count={aging?.['90_plus'] || 0} total={without_so_count} color="bg-red-800" />
          </div>
        </div>
      )}

      {/* MISSING tab — top deals without SO */}
      {activeView === 'missing' && (
        <ScrollArea className="flex-1 px-4 pb-4">
          <div className="space-y-1.5">
            {top_missing.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-700">All Won deals have SOs!</p>
              </div>
            ) : top_missing.map((deal, i) => {
              const urgent = (deal.days_since_won || 0) > 60;
              return (
                <div key={deal.canonical_id || i}
                  className="flex items-start gap-2 p-2.5 rounded-xl border hover:border-[#800000]/20 hover:bg-[#800000]/5 transition-all cursor-pointer group"
                  onClick={() => navigate(`/opportunities?search=${encodeURIComponent(deal.account_name || '')}`)}>
                  {/* Priority indicator */}
                  <div className={`w-1 rounded-full self-stretch shrink-0 ${urgent ? 'bg-red-500' : 'bg-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{deal.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{deal.account_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-500">{deal.owner_name?.split(' ')[0]}</span>
                      {deal.days_since_won && (
                        <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${urgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {deal.days_since_won}d ago
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-[#800000] font-mono">{formatCurrency(deal.sale_value, currency)}</p>
                    <p className="text-[9px] text-muted-foreground">No SO</p>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            className="w-full mt-2 text-xs text-[#800000] font-semibold py-1.5 hover:bg-[#800000]/5 rounded-lg transition-colors"
            onClick={() => navigate('/opportunities?stage=Won')}
            data-testid="view-all-won-btn">
            View all Won opportunities →
          </button>
        </ScrollArea>
      )}

      {/* OWNERS tab — who has the most missing SOs */}
      {activeView === 'owners' && (
        <ScrollArea className="flex-1 px-4 pb-4">
          <div className="space-y-2">
            {by_owner.slice(0, 8).map((owner, i) => {
              const maxVal = by_owner[0]?.value || 1;
              const barW = Math.round(owner.value / maxVal * 100);
              return (
                <div key={owner.name} className="space-y-1" data-testid={`owner-row-${i}`}>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0"
                        style={{ background: 'linear-gradient(135deg, #800000 0%, #c0392b 100%)' }}>
                        {(owner.name || '?')[0].toUpperCase()}
                      </div>
                      <span className="font-medium truncate max-w-32">{owner.name.split(' ').slice(0,2).join(' ')}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-[#800000] font-mono">{formatCurrency(owner.value, currency)}</span>
                      <span className="text-muted-foreground ml-1 text-[10px]">({owner.count} deals)</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${barW}%`, background: 'linear-gradient(90deg, #800000, #c0392b)' }} />
                  </div>
                  {owner.avg_days > 0 && (
                    <p className="text-[9px] text-muted-foreground">Avg {owner.avg_days}d since won</p>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
